import { computeMSDValues } from '@jgp-er-dev/chart-utils'
import merge from 'deepmerge'
import { assocPath, difference, path } from 'ramda'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import _slugify from 'slugify'

import Highcharts from '../../highcharts'
import { getTypeFromSubtype } from '../../utils/indicators'

interface Props {
  initialOptions?: Highcharts.Options
  initialSeries?: Highcharts.SeriesOptionsType[]
  initialCustomizations?: Customizations
  defaultIndicators?: Indicator[]
  title?: string
  reportManagerId?: string
  forceLegend?: { enabled: boolean }
  children?: React.ReactNode
}

export interface ChartContext {
  chartRef: React.RefObject<{
    chart: Highcharts.Chart
    container: React.RefObject<HTMLDivElement>
  }>
  containerRef: React.RefObject<HTMLDivElement>
  options: Highcharts.Options
  setOption: (option: Highcharts.Options) => void
  series: Highcharts.SeriesOptionsType[]
  setSeries: React.Dispatch<
    React.SetStateAction<Highcharts.SeriesOptionsType[]>
  >
  originalSeries: Highcharts.SeriesOptionsType[]
  plotLines: PlotLine[]
  addPlotLine: (plotLine: PlotLine) => void
  removePlotLine: (id: string) => void
  indicators: Indicator[]
  registerIndicator: (indicator: IndicatorArgs) => void
  registerSavedIndicator: (indicator: Indicator) => void
  unregisterIndicator: (id: string) => void
  title: string | undefined
  reportManagerId: string | undefined
  setTitle: React.Dispatch<React.SetStateAction<string | undefined>>
  resetTitle: () => void
  originalTitle: string | undefined
  seriesCustomizations: Record<string, SeriesCustomization>
  setSeriesCustomizations: React.Dispatch<
    React.SetStateAction<Record<string, SeriesCustomization>>
  >
  outliers: Outlier[]
  setOutliers: React.Dispatch<React.SetStateAction<Outlier[]>>
  defaultIndicators: Indicator[]
  registerEvent: (path: string[], id: string, handler: any) => void
}

const ChartContext = createContext<ChartContext>({} as ChartContext)

const useOptions = (initialOptions: Highcharts.Options) => {
  const [optionsQueue, setOptionsQueue] = useState<Highcharts.Options[]>([])
  const [options, setOptions] = useState<Highcharts.Options>(
    initialOptions ?? {}
  )

  const setOption = useCallback(
    (option: Highcharts.Options) =>
      setOptionsQueue((queue) => queue.concat(option)),
    []
  )

  useEffect(() => {
    if (optionsQueue.length > 0) {
      setOptions(merge.all(optionsQueue))
      setOptionsQueue([])
    }
  }, [optionsQueue])

  return { options, setOption }
}

const useSeries = (
  initialSeries: Highcharts.SeriesOptionsType[],
  setOption: (option: Highcharts.Options) => void
) => {
  const [series, setSeries] = useState<Highcharts.SeriesOptionsType[]>(() => {
    const seriesList = initialSeries
    const seriesMissingId = seriesList.filter((s) => !s.id)
    if (seriesMissingId.length > 0) {
      console.warn(
        `All series must be given a unique id for the Chart component to work properly. Please assign an id for the following series: ${seriesMissingId
          .map((s) => s.name)
          .join(', ')}`
      )
    }
    return seriesList
  })

  useEffect(() => setOption({ series }), [series])

  return { series, setSeries }
}

const removeIndicatorPlotLines = (
  chart: Highcharts.Chart,
  plotLines: PlotLine[]
) => {
  const indicatorPlotLines = plotLines
    .map((plotLine) =>
      plotLine.type === 'msd'
        ? [...new Array(5)].map((_, idx) => `${plotLine.id}-${idx}`)
        : []
    )
    .flat()

  chart.yAxis.forEach((axis) => {
    const currentPlotLines = (axis as Highcharts.Axis & {
      plotLinesAndBands: any[]
    }).plotLinesAndBands
      .filter((plotLine) => plotLine.options.type === 'internal')
      .map((plotLine) => plotLine.id)

    const toRemove = difference(currentPlotLines, indicatorPlotLines)

    toRemove.forEach((id) => axis.removePlotLine(id))
  })
}

const usePlotLines = (
  chartRef: React.RefObject<{
    chart: Highcharts.Chart
    container: React.RefObject<HTMLDivElement>
  }>,
  registerEvent: (path: string[], id: string, handler: any) => void
) => {
  const [plotLines, setPlotLines] = useState<PlotLine[]>([])

  const updatePlotLines = useCallback(
    (chart: Highcharts.Chart) => {
      removeIndicatorPlotLines(chart, plotLines)
      plotLines.forEach((config) => {
        const target = chart.get(config.targetId) as
          | (Highcharts.Series & { color: string | undefined })
          | undefined

        if (!target) {
          console.error(`Plotline target with id ${config.targetId} not found`)
          return
        }

        if (!target.visible || !target.points) {
          return
        }

        const validPoints = (target.points as (Highcharts.Point & {
          isInside: boolean
        })[])
          .filter((point) => point.isInside)
          .map((point) => point.y!)

        if (config.type === 'msd') {
          const values = computeMSDValues(validPoints)
          values.forEach((value, idx) => {
            if ((idx === 0 || idx === 4) && !config.options.sd2) {
              return
            }
            if ((idx === 1 || idx === 3) && !config.options.sd1) {
              return
            }
            if (idx === 2 && !config.options.avg) {
              return
            }

            const id = `${config.id}-${idx}`
            target.yAxis.addPlotLine({
              id,
              value,
              color: target.color,
              dashStyle: idx === 2 ? 'ShortDot' : 'Dash',
              width: idx === 2 ? 3 : 1,
              label: {
                text: `${
                  idx === 2 ? 'Média' : (idx - 2).toString() + 'σ'
                }: ${value.toFixed(1)}`,
                style: { fontWeight: idx === 2 ? 'bold' : 'regular' },
              },
              zIndex: 10,
              type: 'internal',
            } as Highcharts.YAxisPlotLinesOptions)
          })
        }
      })
    },
    [plotLines]
  )

  useEffect(() => {
    chartRef.current && updatePlotLines(chartRef.current.chart)
  }, [updatePlotLines])

  useEffect(() => {
    registerEvent(
      ['xAxis', 'events', 'afterSetExtremes'],
      'update-plotlines-onExtremesChange',
      (ctx: any) => {
        updatePlotLines(ctx.chart)
      }
    )
    registerEvent(
      ['plotOptions', 'series', 'events', 'legendItemClick'],
      'update-plotlines-onLegendItemClick',
      (ctx: any) => {
        // Delay it so series gets removed/inserted before plotlines are updated
        setTimeout(() => updatePlotLines(ctx.chart))
      }
    )
  }, [updatePlotLines])

  const addPlotLine = useCallback((plotLine: PlotLine) => {
    setPlotLines((curPlotLines) => curPlotLines.concat(plotLine))
  }, [])

  const removePlotLine = useCallback((id: string) => {
    setPlotLines((curPlotLines) =>
      curPlotLines.filter((plotLine) => plotLine.id !== id)
    )
  }, [])

  return { plotLines, addPlotLine, removePlotLine }
}

// This just keeps track of the indicators added by the user. The methods provided do *not* handle
// chart indicator data.
const useIndicators = (initialIndicators: Indicator[]) => {
  const [indicators, setIndicators] = useState<Indicator[]>(initialIndicators)

  const registerIndicator = useCallback((args: IndicatorArgs) => {
    const { id, targetId, name, ...step } = args
    const type = getTypeFromSubtype(args.subtype)

    if (!type) {
      console.error(`Invalid indicator subtype: ${args.subtype}`)
      return
    }

    setIndicators((curIndicators) => {
      if (type === 'series') {
        const targetIndicator = curIndicators.find(
          (indicator) => indicator.id === targetId
        )
        return curIndicators.concat(
          targetIndicator
            ? {
                ...step,
                id,
                targetId: targetIndicator.targetId,
                name,
                type,
                steps: targetIndicator.steps.concat(step),
              }
            : {
                ...step,
                id,
                targetId,
                name,
                type,
                steps: [step],
              }
        )
      } else {
        return curIndicators.concat({
          ...step,
          id,
          targetId,
          name,
          type,
          steps: [step],
        })
      }
    })
  }, [])

  const registerSavedIndicator = useCallback(
    (indicator: Indicator) =>
      setIndicators((curIndicators) => curIndicators.concat(indicator)),
    []
  )

  const unregisterIndicator = useCallback(
    (id: string) =>
      setIndicators((curIndicators) =>
        curIndicators.filter((indicator) => indicator.id !== id)
      ),
    []
  )

  return {
    indicators,
    registerSavedIndicator,
    registerIndicator,
    unregisterIndicator,
  }
}

const useEditableTitle = (
  propsTitle: string | undefined,
  initialTitle: string | undefined,
  setOption: (option: Highcharts.Options) => void
) => {
  const [title, setTitle] = useState<string | undefined>(initialTitle)

  useEffect(
    () =>
      setOption({
        exporting: {
          chartOptions: {
            title: {
              text: title,
            },
          },
          filename:
            title &&
            _slugify(title, {
              lower: true,
              locale: 'pt',
            }),
        },
      }),
    [title]
  )

  const resetTitle = useCallback(() => setTitle(propsTitle), [propsTitle])

  return { title, setTitle, resetTitle }
}

// Only keeps track of customizations. Does not actually perform changes.
const useSeriesCustomizations = (
  initialSeriesCustomizations: Record<string, SeriesCustomization>
) => {
  const [seriesCustomizations, setSeriesCustomizations] = useState<
    Record<string, SeriesCustomization>
  >(initialSeriesCustomizations)
  return { seriesCustomizations, setSeriesCustomizations }
}

const useOutliers = (initialOutliers: Outlier[]) => {
  const [outliers, setOutliers] = useState<Outlier[]>(initialOutliers)
  return { outliers, setOutliers }
}

// Handles chart event handlers
const useEvents = (setOption: (option: Highcharts.Options) => void) => {
  const [events, setEvents] = useState<
    Record<string, { path: string[]; handler: any }>
  >({})

  const registerEvent = useCallback(
    (path: string[], id: string, handler: any) => {
      setEvents((curEvents) => ({ ...curEvents, [id]: { path, handler } }))
    },
    []
  )

  useEffect(() => {
    let option = {}
    Object.values(events).forEach(({ path: eventPath, handler }) => {
      const curValue = path(eventPath, option) as any
      if (curValue) {
        const mergedFunction = function (this: any, ...args: any[]) {
          curValue(this, ...args)
          handler(this, ...args)
        }
        option = assocPath(eventPath, mergedFunction, option)
      } else {
        option = assocPath(eventPath, handler, option)
      }
    })

    setOption(option)
  }, [events, setOption])

  return registerEvent
}

const useScrollbar = (
  setOption: (option: Highcharts.Options) => void,
  registerEvent: (path: string[], id: string, handler: any) => void
) => {
  useEffect(() => {
    registerEvent(
      ['xAxis', 'events', 'afterSetExtremes'],
      'hide-show-scrollbar',
      (ctx: any) => {
        if (ctx.dataMin === ctx.min && ctx.dataMax === ctx.max) {
          if (ctx.chart.options.scrollbar.enabled) {
            setOption({ scrollbar: { enabled: false } })
          }
        } else {
          if (!ctx.chart.options.scrollbar.enabled) {
            setOption({ scrollbar: { enabled: true } })
          }
        }
      }
    )
  }, [registerEvent, setOption])
}

const useLegend = (
  series: Highcharts.SeriesOptionsType[],
  forceLegend: { enabled: boolean } | undefined,
  setOption: (option: Highcharts.Options) => void
) => {
  const lastSeriesLength = useRef<number>(-1)

  useEffect(() => {
    if (lastSeriesLength.current === series.length) {
      return
    }
    lastSeriesLength.current = series.length

    if (forceLegend) {
      setOption({ legend: { enabled: forceLegend.enabled } })
    } else {
      if (series.length <= 1) {
        setOption({ legend: { enabled: false } })
      } else {
        setOption({ legend: { enabled: true } })
      }
    }
  }, [series, forceLegend])
}

export const ChartContextProvider: React.FC<Props> = (props) => {
  const chartRef = useRef<{
    chart: Highcharts.Chart
    container: React.RefObject<HTMLDivElement>
  }>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const { options, setOption } = useOptions(props.initialOptions ?? {})

  const { series, setSeries } = useSeries(props.initialSeries ?? [], setOption)

  const registerEvent = useEvents(setOption)

  const { plotLines, addPlotLine, removePlotLine } = usePlotLines(
    chartRef,
    registerEvent
  )

  const { title, setTitle, resetTitle } = useEditableTitle(
    props.title,
    props.initialCustomizations?.title ?? props.title,
    setOption
  )

  const {
    indicators,
    registerIndicator,
    registerSavedIndicator,
    unregisterIndicator,
  } = useIndicators(props.initialCustomizations?.indicators ?? [])

  const {
    seriesCustomizations,
    setSeriesCustomizations,
  } = useSeriesCustomizations(props.initialCustomizations?.series ?? {})

  const { outliers, setOutliers } = useOutliers(
    props.initialCustomizations?.outliers ?? []
  )

  useScrollbar(setOption, registerEvent)

  useLegend(series, props.forceLegend, setOption)

  const [defaultIndicators] = useState<Indicator[]>(
    props.defaultIndicators ?? []
  )

  const value = useMemo(
    () => ({
      chartRef,
      containerRef,
      options,
      setOption,
      series,
      setSeries,
      originalSeries: props.initialSeries ?? [],
      plotLines,
      addPlotLine,
      removePlotLine,
      indicators,
      registerIndicator,
      registerSavedIndicator,
      unregisterIndicator,
      title,
      setTitle,
      resetTitle,
      originalTitle: props.title,
      reportManagerId: props.reportManagerId,
      seriesCustomizations,
      setSeriesCustomizations,
      outliers,
      setOutliers,
      defaultIndicators,
      registerEvent,
    }),
    [
      options,
      setOption,
      series,
      props.initialSeries,
      plotLines,
      addPlotLine,
      removePlotLine,
      indicators,
      registerIndicator,
      registerSavedIndicator,
      unregisterIndicator,
      title,
      resetTitle,
      props.title,
      seriesCustomizations,
      outliers,
      defaultIndicators,
      registerEvent,
    ]
  )

  // Solely for debugging purposes
  useEffect(() => {
    ;(window as any).chart = chartRef.current
  }, [])

  return (
    <ChartContext.Provider value={value}>
      {props.children}
    </ChartContext.Provider>
  )
}

export const useChart = () => useContext(ChartContext)
